import { FlexBox, useToasts } from "@brg/ui";
import styled from "styled-components";
import { FC } from 'react';
import { useTranslation } from "react-i18next";
import { IconCloseMob } from "../SelectGeoLocationTagsModal/SelectGeoLocationTagsModal";
import { LayoutConst, useModal } from "../../../core";
import { RectangularButtonFilled } from "shared/components/buttons";
import { Colors } from "@brg/ui";
import { FlowType } from "../../features/QueuePaymenrt/constants/flowType";
import { useDispatch, useSelector } from "react-redux";
import { setImmediatePurchaseProps, clearOrderFields, selectorOrder } from "../../features/QueuePaymenrt/slices/OrderSlice";
import { EntityPaymentTypes } from "../../features/QueuePaymenrt/constants/payment";
import { CurrencyType, API, Endpoints } from "@brg/api";

const Wrapper = styled(FlexBox)`
    width: 560px;
    background: #FEFEFE;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    position: relative;
    padding: 32px 40px 40px 40px;
    flex-flow: column;

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 16px;
    width: 300px;
  }
`;

const Title = styled.div`
    font-family: 'Inter';
    font-weight: 600;
    font-size: 28px;
    line-height: 34px;
    letter-spacing: -0.02em;
    color: #151514;
    margin-bottom: 8px;
  
    @media (max-width: ${LayoutConst.screen.s}px) {
      font-weight: 600;
      font-size: 17px;
      line-height: 22px;
      letter-spacing: normal;
    }
`;

const Content = styled(FlexBox)`
    margin-bottom: 32px;
`;

const InfoItem = styled.div`
    font-family: 'Inter';
    font-style: normal;
    font-weight: 400;
    font-size: 17px;
    line-height: 22px;
    letter-spacing: -0.02em;
    color: #151514;
    display: flex;
    align-items: flex-start;

    &:last-child {
        margin-bottom: 0;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
      font-size: 13px;
      line-height: 16px;
    }
`;

const BulletPoint = styled.div`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #151514;
    margin-right: 12px;
    margin-top: 8px;
    flex-shrink: 0;

    @media (max-width: ${LayoutConst.screen.s}px) {
      margin-top: 6px;
    }
`;

const PriceText = styled.span`
    font-weight: 600;
`;

const ButtonContainer = styled(FlexBox)`
    flex-flow: row;
    gap: 16px;
    width: 100%;
`;

const CancelButton = styled(RectangularButtonFilled)`
    flex: 1;
    padding: 13px 24px;
    font-weight: 600;
    font-size: 17px;
    line-height: 22px;
    height: 50px !important;
    letter-spacing: -0.02em;
    background-color: transparent;
    border: 1px solid ${Colors.galBlack47};
    color: ${Colors.galBlack100};

    &:hover {
        background-color: ${Colors.galBlack47};
        color: ${Colors.geoWhite100};
    }
`;

const ConfirmButton = styled(RectangularButtonFilled)`
    flex: 1;
    padding: 13px 24px;
    font-weight: 600;
    font-size: 17px;
    line-height: 22px;
    height: 50px !important;
    letter-spacing: -0.02em;
    background-color: ${Colors.galBlack100};
    border: 1px solid ${Colors.galBlack100};
    color: ${Colors.geoWhite100};

    &:hover {
        background-color: ${Colors.galBlack47};
        border-color: ${Colors.galBlack47};
    }
`;

export const CloseWrapper = styled.div`
    position: absolute;
    top: 16px;
    right: 16px;
    cursor: pointer;
    z-index: 1;

    @media (max-width: ${LayoutConst.screen.s}px) {
        top: 12px;
        right: 12px;
    }
`;

interface ConfirmationModalProps {
    projectName?: string;
    competitionId?: string;
    competitionPrice?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    onPaymentSuccess?: () => void;
    onPaymentFail?: () => void;
    projectId?: string;
    selectedProjectId?: string;
}

export const ConfirmationModal: FC<ConfirmationModalProps> = ({ 
    projectName = '', 
    competitionId = '',
    competitionPrice = '',
    onConfirm,
    onCancel,
    onPaymentSuccess,
    onPaymentFail,
    projectId,
    selectedProjectId
}) => {
    const { t } = useTranslation();
    const modal = useModal();
    const dispatch = useDispatch();
    const { availableTariffs, tariffsLoading, activeTariff, error: orderError, immediatePurchaseProps, bookCard, bookCardT } = useSelector(selectorOrder)
    const toasts = useToasts()
    const handleConfirm = async () => {
        onConfirm?.();
        modal.hide();
        
        try {
            dispatch(clearOrderFields());
            
            // Загружаем данные о карточке конкурса с сервера
            const competitionCardResponse = await API.request()
                .endpoint(Endpoints.competitions)
                .url('/api/Competition/GetCompetitionCard')
                .addQuery('competitionId', competitionId)
                .get()
                .send();
            
            const competitionCard = await competitionCardResponse.json();
            
            // Извлекаем цену из priceInfo
            const cardPrice = competitionCard?.priceInfo?.realProductPrice || parseInt(competitionPrice) || 2500;
            const cardName = Array.isArray(competitionCard?.name) 
                ? competitionCard.name.find(n => n.lang === 'Ru')?.name || competitionCard.name[0]?.name
                : competitionCard?.name;

            // Загружаем данные о конкурсе в Redux store с реальными данными
            dispatch(setImmediatePurchaseProps({
                productId: competitionCard?.cardId,
                paymentEntityType: EntityPaymentTypes.product_card,
                flowType: FlowType.COPY_CARD,
                currencyType: CurrencyType.RealMoney,
                tariff: activeTariff,
                price: cardPrice,
                productInfo: {
                    id: competitionCard?.cardId,
                    name: cardName || `Участие в конкурсе: ${projectName}`,
                    preview: competitionCard?.images || [],
                    price: cardPrice,
                    type: EntityPaymentTypes.product_card,
                    typeCard: "product", // Важно! Это поле проверяется в CheckOut.tsx
                    summary: {
                        'Проект': projectName,
                        'Стоимость': cardPrice + '₽'
                    }
                }
            }));
            
            // Открываем PaymentManager для оплаты участия в конкурсе
            modal.show("paymentManager", {
                containerStyles: {
                    background: 'rgba(231,231,231,1)',
                    backdropFilter: 'none'
                },
                flowType: FlowType.COPY_CARD,
                sharedProps: {
                    competitionId: competitionId,
                    selectedProjectId: selectedProjectId
                },
                onPaymentSuccess: () => {
                    console.log('Оплата за участие в конкурсе прошла успешно');
                    onPaymentSuccess?.();
                },
                onPaymentFail: () => {
                    console.log('Ошибка оплаты за участие в конкурсе');
                    onPaymentFail?.();
                },
                cancel: () => {
                    dispatch(clearOrderFields());
                }
            });
        } catch (error) {
            console.error('Ошибка при загрузке данных о конкурсе:', error);
            toasts.error('Ошибка при загрузке данных о конкурсе')
        }
    };

    const handleCancel = () => {
        console.log('Отмена отправки');
        onCancel?.();
        modal.hide();
    };

    return (
        <Wrapper>
            <CloseWrapper>
                <IconCloseMob onClick={() => modal.hide()}/>
            </CloseWrapper>
            
            <Title>Подтверждение отправки</Title>
            
            <Content gy={4} direction="column">
                <InfoItem>
                    <BulletPoint />
                    <div>
                        Стоимость подачи проекта на конкурс <PriceText>{competitionPrice} ₽</PriceText>
                    </div>
                </InfoItem>
                <InfoItem>
                    <BulletPoint />
                    <div>
                        После оплаты проект будет отправлен на модерацию и станет недоступен для редактирования
                    </div>
                </InfoItem>
            </Content>

            <ButtonContainer>
                <CancelButton onClick={handleCancel}>
                    Отменить
                </CancelButton>
                <ConfirmButton onClick={handleConfirm}>
                    Перейти к оплате
                </ConfirmButton>
            </ButtonContainer>
        </Wrapper>
    );
};
import { FlexBox, useToasts } from "@brg/ui";
import styled from "styled-components";
import { FC } from 'react';
import { useTranslation } from "react-i18next";
import { IconCloseMob } from "../SelectGeoLocationTagsModal/SelectGeoLocationTagsModal";
import { LayoutConst, useModal } from "../../../core";
import { RectangularButtonFilled } from "shared/components/buttons";
import { Colors } from "@brg/ui";
import { FlowType } from "../../features/QueuePaymenrt/constants/flowType";
import { useDispatch, useSelector } from "react-redux";
import { setImmediatePurchaseProps, clearOrderFields, selectorOrder } from "../../features/QueuePaymenrt/slices/OrderSlice";
import { EntityPaymentTypes } from "../../features/QueuePaymenrt/constants/payment";
import { CurrencyType, API, Endpoints } from "@brg/api";

const Wrapper = styled(FlexBox)`
    width: 560px;
    background: #FEFEFE;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    position: relative;
    padding: 32px 40px 40px 40px;
    flex-flow: column;

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 16px;
    width: 300px;
  }
`;

const Title = styled.div`
    font-family: 'Inter';
    font-weight: 600;
    font-size: 28px;
    line-height: 34px;
    letter-spacing: -0.02em;
    color: #151514;
    margin-bottom: 8px;
  
    @media (max-width: ${LayoutConst.screen.s}px) {
      font-weight: 600;
      font-size: 17px;
      line-height: 22px;
      letter-spacing: normal;
    }
`;

const Content = styled(FlexBox)`
    margin-bottom: 32px;
`;

const InfoItem = styled.div`
    font-family: 'Inter';
    font-style: normal;
    font-weight: 400;
    font-size: 17px;
    line-height: 22px;
    letter-spacing: -0.02em;
    color: #151514;
    display: flex;
    align-items: flex-start;

    &:last-child {
        margin-bottom: 0;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
      font-size: 13px;
      line-height: 16px;
    }
`;

const BulletPoint = styled.div`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #151514;
    margin-right: 12px;
    margin-top: 8px;
    flex-shrink: 0;

    @media (max-width: ${LayoutConst.screen.s}px) {
      margin-top: 6px;
    }
`;

const PriceText = styled.span`
    font-weight: 600;
`;

const ButtonContainer = styled(FlexBox)`
    flex-flow: row;
    gap: 16px;
    width: 100%;
`;

const CancelButton = styled(RectangularButtonFilled)`
    flex: 1;
    padding: 13px 24px;
    font-weight: 600;
    font-size: 17px;
    line-height: 22px;
    height: 50px !important;
    letter-spacing: -0.02em;
    background-color: transparent;
    border: 1px solid ${Colors.galBlack47};
    color: ${Colors.galBlack100};

    &:hover {
        background-color: ${Colors.galBlack47};
        color: ${Colors.geoWhite100};
    }
`;

const ConfirmButton = styled(RectangularButtonFilled)`
    flex: 1;
    padding: 13px 24px;
    font-weight: 600;
    font-size: 17px;
    line-height: 22px;
    height: 50px !important;
    letter-spacing: -0.02em;
    background-color: ${Colors.galBlack100};
    border: 1px solid ${Colors.galBlack100};
    color: ${Colors.geoWhite100};

    &:hover {
        background-color: ${Colors.galBlack47};
        border-color: ${Colors.galBlack47};
    }
`;

export const CloseWrapper = styled.div`
    position: absolute;
    top: 16px;
    right: 16px;
    cursor: pointer;
    z-index: 1;

    @media (max-width: ${LayoutConst.screen.s}px) {
        top: 12px;
        right: 12px;
    }
`;

interface ConfirmationModalProps {
    projectName?: string;
    competitionId?: string;
    competitionPrice?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    onPaymentSuccess?: () => void;
    onPaymentFail?: () => void;
    projectId?: string;
    selectedProjectId?: string;
}

export const ConfirmationModal: FC<ConfirmationModalProps> = ({ 
    projectName = '', 
    competitionId = '',
    competitionPrice = '',
    onConfirm,
    onCancel,
    onPaymentSuccess,
    onPaymentFail,
    projectId,
    selectedProjectId
}) => {
    const { t } = useTranslation();
    const modal = useModal();
    const dispatch = useDispatch();
    const { availableTariffs, tariffsLoading, activeTariff, error: orderError, immediatePurchaseProps, bookCard, bookCardT } = useSelector(selectorOrder)
    const toasts = useToasts()
    const handleConfirm = async () => {
        onConfirm?.();
        modal.hide();
        
        try {
            dispatch(clearOrderFields());
            
            // Загружаем данные о карточке конкурса с сервера
            const competitionCardResponse = await API.request()
                .endpoint(Endpoints.competitions)
                .url('/api/Competition/GetCompetitionCard')
                .addQuery('competitionId', competitionId)
                .get()
                .send();
            
            const competitionCard = await competitionCardResponse.json();
            
            // Извлекаем цену из priceInfo
            const cardPrice = competitionCard?.priceInfo?.realProductPrice || parseInt(competitionPrice) || 2500;
            const cardName = Array.isArray(competitionCard?.name) 
                ? competitionCard.name.find(n => n.lang === 'Ru')?.name || competitionCard.name[0]?.name
                : competitionCard?.name;

            // Загружаем данные о конкурсе в Redux store с реальными данными
            dispatch(setImmediatePurchaseProps({
                productId: competitionCard?.cardId,
                paymentEntityType: EntityPaymentTypes.product_card,
                flowType: FlowType.COPY_CARD,
                currencyType: CurrencyType.RealMoney,
                tariff: activeTariff,
                price: cardPrice,
                productInfo: {
                    id: competitionCard?.cardId,
                    name: cardName || `Участие в конкурсе: ${projectName}`,
                    preview: competitionCard?.images || [],
                    price: cardPrice,
                    type: EntityPaymentTypes.product_card,
                    typeCard: "product", // Важно! Это поле проверяется в CheckOut.tsx
                    summary: {
                        'Проект': projectName,
                        'Стоимость': cardPrice + '₽'
                    }
                }
            }));
            
            // Открываем PaymentManager для оплаты участия в конкурсе
            modal.show("paymentManager", {
                containerStyles: {
                    background: 'rgba(231,231,231,1)',
                    backdropFilter: 'none'
                },
                flowType: FlowType.COPY_CARD,
                sharedProps: {
                    competitionId: competitionId,
                    selectedProjectId: selectedProjectId
                },
                onPaymentSuccess: () => {
                    console.log('Оплата за участие в конкурсе прошла успешно');
                    onPaymentSuccess?.();
                },
                onPaymentFail: () => {
                    console.log('Ошибка оплаты за участие в конкурсе');
                    onPaymentFail?.();
                },
                cancel: () => {
                    dispatch(clearOrderFields());
                }
            });
        } catch (error) {
            console.error('Ошибка при загрузке данных о конкурсе:', error);
            toasts.error('Ошибка при загрузке данных о конкурсе')
        }
    };

    const handleCancel = () => {
        console.log('Отмена отправки');
        onCancel?.();
        modal.hide();
    };

    return (
        <Wrapper>
            <CloseWrapper>
                <IconCloseMob onClick={() => modal.hide()}/>
            </CloseWrapper>
            
            <Title>Подтверждение отправки</Title>
            
            <Content gy={4} direction="column">
                <InfoItem>
                    <BulletPoint />
                    <div>
                        Стоимость подачи проекта на конкурс <PriceText>{competitionPrice} ₽</PriceText>
                    </div>
                </InfoItem>
                <InfoItem>
                    <BulletPoint />
                    <div>
                        После оплаты проект будет отправлен на модерацию и станет недоступен для редактирования
                    </div>
                </InfoItem>
            </Content>

            <ButtonContainer>
                <CancelButton onClick={handleCancel}>
                    Отменить
                </CancelButton>
                <ConfirmButton onClick={handleConfirm}>
                    Перейти к оплате
                </ConfirmButton>
            </ButtonContainer>
        </Wrapper>
    );
};
